import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { CloseIcon } from "@excalidraw/excalidraw/components/icons";

import {
  deleteProject,
  listProjects,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  registerPanelOpener,
  renameProject,
  useCloudState,
} from "./api";
import { cloudSync } from "./CloudSync";
import "./cloud.scss";

import type { ProjectMeta } from "./api";

type View = "projects" | "login" | "register";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export const CloudPanel = () => {
  const { user, activeProject, status, sessionExpired } = useCloudState();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("projects");
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [projects, setProjects] = useState<ProjectMeta[] | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    // registra handler global para abrir o painel a partir do menu
    registerPanelOpener(() => {
      setOpen(true);
      setView("projects");
      setError("");
    });
    return () => registerPanelOpener(() => {});
  }, []);

  // foca o primeiro campo ao abrir o painel de login/cadastro
  useEffect(() => {
    if (open && !user && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [open, user, view]);

  useEffect(() => {
    if (!user) {
      setView("login");
    }
  }, [user]);

  const refreshProjects = useCallback(async () => {
    if (!user) {
      return;
    }
    setLoadingProjects(true);
    try {
      const list = await listProjects();
      setProjects(list);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoadingProjects(false);
    }
  }, [user]);

  useEffect(() => {
    if (open && user && view === "projects") {
      void refreshProjects();
    }
  }, [open, user, view, refreshProjects]);

  if (!open) {
    return null;
  }

  const close = () => {
    setOpen(false);
    setError("");
    cloudSync.flushNow();
  };

  const handleAuth = async (mode: "login" | "register") => {
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await apiLogin(email.trim(), password);
      } else {
        await apiRegister(email.trim(), password, name.trim());
      }
      setEmail("");
      setPassword("");
      setName("");
      setView("projects");
    } catch (e: any) {
      setError(e.message || "Falha na autenticação.");
    } finally {
      setBusy(false);
    }
  };

  const handleNewProject = async () => {
    const projectName =
      window.prompt("Nome do novo projeto:", "Sem título")?.trim() || "";
    if (!projectName) {
      return;
    }
    setError("");
    setBusy(true);
    try {
      await cloudSync.newProject(projectName);
      await refreshProjects();
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveAsNew = async () => {
    const projectName =
      window.prompt("Nome da cópia a salvar:", "Cópia")?.trim() || "";
    if (!projectName) {
      return;
    }
    setError("");
    setBusy(true);
    try {
      await cloudSync.saveAsNew(projectName);
      await refreshProjects();
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleOpen = async (meta: ProjectMeta) => {
    setError("");
    setBusy(true);
    try {
      await cloudSync.openProject(meta);
      setOpen(false);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (meta: ProjectMeta) => {
    const newName = window.prompt("Novo nome do projeto:", meta.name)?.trim();
    if (!newName || newName === meta.name) {
      return;
    }
    setError("");
    try {
      await renameProject(meta.id, newName);
      await refreshProjects();
    } catch (e: any) {
      setError(e.message || String(e));
    }
  };

  const handleDelete = async (meta: ProjectMeta) => {
    if (
      !window.confirm(
        `Excluir o projeto "${meta.name}"? Esta ação não pode ser desfeita.`,
      )
    ) {
      return;
    }
    setError("");
    try {
      await deleteProject(meta.id);
      await refreshProjects();
    } catch (e: any) {
      setError(e.message || String(e));
    }
  };

  const statusLabel =
    status === "saving"
      ? "Salvando…"
      : status === "saved"
      ? "Salvo na nuvem"
      : status === "error"
      ? "Erro ao salvar"
      : "";

  // Renderizado fora da árvore do Excalidraw (portal no body) e com
  // propagação de teclado interrompida, para que digitar nos campos não
  // dispare os atalhos do canvas.
  return createPortal(
    <div
      className="x3-cloud-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
      onKeyDown={(event) => event.stopPropagation()}
      onKeyUp={(event) => event.stopPropagation()}
    >
      <div className="x3-cloud-panel" role="dialog" aria-label="Conta">
        <div className="x3-cloud-header">
          <h2>
            {user
              ? `Minha conta — ${user.email}`
              : sessionExpired
              ? "Sessão expirada — entre novamente"
              : "Entrar no Quadro X3"}
          </h2>
          <button onClick={close} aria-label="Fechar">
            {CloseIcon}
          </button>
        </div>

        <div className="x3-cloud-body">
          {!user && (
            <>
              <div className="x3-tabs">
                <button
                  className={view === "login" ? "x3-tab-active" : ""}
                  onClick={() => setView("login")}
                >
                  Entrar
                </button>
                <button
                  className={view === "register" ? "x3-tab-active" : ""}
                  onClick={() => setView("register")}
                >
                  Criar conta
                </button>
              </div>

              {view === "register" && (
                <div>
                  <label htmlFor="x3-name">Nome</label>
                  <input
                    id="x3-name"
                    type="text"
                    ref={firstFieldRef}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome"
                    autoComplete="name"
                  />
                </div>
              )}

              <div>
                <label htmlFor="x3-email">E-mail</label>
                <input
                  id="x3-email"
                  type="email"
                  ref={view !== "register" ? firstFieldRef : undefined}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@empresa.com.br"
                  autoComplete="email"
                />
              </div>

              <div>
                <label htmlFor="x3-password">Senha</label>
                <input
                  id="x3-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete={
                    view === "register" ? "new-password" : "current-password"
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && email && password && !busy) {
                      void handleAuth(
                        view === "register" ? "register" : "login",
                      );
                    }
                  }}
                />
              </div>

              {error && <div className="x3-cloud-error">{error}</div>}

              <div className="x3-cloud-actions">
                <button
                  className="x3-btn x3-primary"
                  disabled={busy || !email || !password}
                  onClick={() =>
                    handleAuth(view === "register" ? "register" : "login")
                  }
                >
                  {view === "register" ? "Criar conta" : "Entrar"}
                </button>
              </div>

              <small>
                Seus projetos ficam salvos na sua conta e podem ser abertos em
                qualquer navegador ou computador.
              </small>
            </>
          )}

          {user && (
            <>
              <div className="x3-cloud-actions">
                <button
                  className="x3-btn x3-primary"
                  disabled={busy}
                  onClick={() => void handleNewProject()}
                >
                  + Novo projeto
                </button>
                <button
                  className="x3-btn"
                  disabled={busy}
                  onClick={() => void handleSaveAsNew()}
                >
                  Salvar cena atual como cópia
                </button>
                <button
                  className="x3-btn"
                  onClick={() => {
                    apiLogout();
                    setProjects(null);
                  }}
                >
                  Sair
                </button>
              </div>

              {(activeProject || statusLabel) && (
                <div className="x3-status-line">
                  {activeProject ? (
                    <>
                      Projeto atual: <strong>{activeProject.name}</strong>
                      {statusLabel ? ` — ${statusLabel}` : ""}
                    </>
                  ) : (
                    <>
                      Nenhum projeto aberto.
                      {statusLabel ? ` ${statusLabel}` : ""} Desenhe e use
                      “Salvar cena atual como cópia” para guardar na nuvem.
                    </>
                  )}
                </div>
              )}

              {sessionExpired && (
                <div className="x3-cloud-success-note">
                  Sessão anterior expirou. Faça login novamente para continuar
                  salvando.
                </div>
              )}

              {error && <div className="x3-cloud-error">{error}</div>}

              {loadingProjects && <div className="x3-empty">Carregando…</div>}

              {!loadingProjects && projects && projects.length === 0 && (
                <div className="x3-empty">
                  Você ainda não tem projetos. Clique em “+ Novo projeto” para
                  começar.
                </div>
              )}

              {!loadingProjects && projects && projects.length > 0 && (
                <ul className="x3-project-list">
                  {projects.map((p) => (
                    <li
                      key={p.id}
                      className={`x3-project-item${
                        activeProject?.id === p.id ? " x3-active" : ""
                      }`}
                    >
                      <div className="x3-project-info">
                        <strong title={p.name}>{p.name}</strong>
                        <small>Atualizado em {formatDate(p.updatedAt)}</small>
                      </div>
                      <div className="x3-project-buttons">
                        <button
                          className="x3-btn x3-primary"
                          disabled={busy}
                          onClick={() => void handleOpen(p)}
                        >
                          Abrir
                        </button>
                        <button
                          className="x3-btn"
                          disabled={busy}
                          onClick={() => void handleRename(p)}
                        >
                          Renomear
                        </button>
                        <button
                          className="x3-btn"
                          disabled={busy}
                          onClick={() => void handleDelete(p)}
                        >
                          Excluir
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
